import { ChangeEvent, memo, useMemo } from 'react';
import * as Yup from 'yup';
import { Field, Formik, FormikHelpers } from 'formik';
import { toast } from 'react-toastify';

import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';

import { createNewGroup, isNewGroup } from '@modules/api';
import { userMessage } from '@modules/api-error';
import { logError } from '@modules/logging';
import { APP_ENV } from '@modules/consts';
import { DialogSubmitButtons } from './DialogSubmitButtons';

import CreateInterviewInput from './CreateInterviewInput';
import { CreateInterviewBody, getIdFromName } from '@synergetx/puppet-interview-types';

export interface CreateGroupForm extends CreateInterviewBody {
    groupName: string;
}

interface Props {
    onClose: () => void;
    baseInterviewId?: string;
    baseGroupId?: string;
}

const addInterviewValidationSchema = Yup.object<CreateGroupForm>({
    name: Yup.string().label('Name').required('Name is required').min(1, 'Name is required'),
    groupName: Yup.string()
        .label('Project Group Name')
        .required('Project Group Name is required')
        .min(4, 'Project Group Name must be at least 4 characters')
        // Yup runs custom tests for `undefined` too, so guard rather than calling `.match` on it.
        .test('allowed-characters', 'Letters, numbers and spaces only', (v) =>
            v ? /^[a-zA-Z0-9\s]*$/.test(v) : true,
        )
        .test(
            'group-exists',
            ({ value }) => `A project group named "${value}" already exists`,
            isNewGroup,
        ),

    interviewToDuplicate: Yup.object(),
});

const AddNewGroupDialog = ({ onClose, baseGroupId = '', baseInterviewId = '' }: Props) => {
    const initialValues: CreateGroupForm = useMemo(
        () => ({
            baseInterviewId,
            baseGroupId,
            groupName: '',
            groupId: '',
            name: '',
        }),
        [baseGroupId, baseInterviewId],
    );

    const onSubmit = async (
        values: CreateGroupForm,
        { setSubmitting }: FormikHelpers<CreateGroupForm>,
    ) => {
        setSubmitting(true);

        try {
            await toast.promise(createNewGroup(values), {
                error: {
                    render: ({ data }) =>
                        userMessage(data, 'Error creating new project. Please try again.'),
                },
                pending: `Creating new project and interview...`,
                success: 'Success! Project and interview created!',
            });

            onClose();
        } catch (error) {
            // Leave the dialog open with what was typed so it can be retried.
            logError(error as Error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            enableReinitialize
            validateOnChange={true}
            validationSchema={addInterviewValidationSchema}
        >
            {({ errors, dirty, submitForm, setFieldValue, isSubmitting, isValidating }) => (
                <Dialog maxWidth="md" fullWidth open onClose={onClose}>
                    {/* <Dialog maxWidth="md" fullWidth open onClose={() => setOpen(undefined)}> */}
                    <DialogTitle>Create a new Project Group</DialogTitle>

                    {isSubmitting ? (
                        <CircularProgress style={{ alignSelf: 'center', margin: '5rem' }} />
                    ) : (
                        <>
                            <DialogContent dividers>
                                <Stack spacing={2}>
                                    <>
                                        <Box>
                                            <DialogContentText
                                                sx={{
                                                    mb: 2,
                                                }}
                                            >
                                                {`First, Enter an ID for the new Project Group (e.g.
                                                ${APP_ENV === 'sabol' ? 'California, Michigan' : 'Colombia, Haiti'})`}
                                            </DialogContentText>
                                        </Box>
                                        <Box>
                                            {/* Errors go in `helperText` with `error` on the field
                                                — via `slotProps.input` TextField never reads it. */}
                                            <Field
                                                as={TextField}
                                                name="groupName"
                                                label="New Project Group Name"
                                                fullWidth
                                                required
                                                placeholder="Enter new Project Group Name..."
                                                error={
                                                    dirty && !isValidating && !!errors?.groupName
                                                }
                                                helperText={
                                                    isValidating
                                                        ? 'Checking availability…'
                                                        : dirty && errors?.groupName
                                                          ? errors.groupName
                                                          : 'Letters, numbers and spaces only'
                                                }
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    setFieldValue('groupName', e.target.value);
                                                    // auto-generate groupId from group name
                                                    setFieldValue(
                                                        'groupId',
                                                        getIdFromName(e.target.value),
                                                    );
                                                }}
                                            />
                                        </Box>
                                    </>
                                    <Box>
                                        <DialogContentText
                                            sx={{
                                                mb: 2,
                                            }}
                                        >
                                            Second, add the first Survey for this new Group
                                        </DialogContentText>
                                    </Box>
                                    <Box>
                                        <CreateInterviewInput />
                                    </Box>
                                </Stack>
                            </DialogContent>
                            <DialogSubmitButtons
                                submitDisabled={
                                    !dirty || isValidating || Object.keys(errors).length > 0
                                }
                                onSubmit={submitForm}
                                onClose={onClose}
                            />
                        </>
                    )}
                </Dialog>
            )}
        </Formik>
    );
};

export default memo(AddNewGroupDialog);
